import { useEffect, useState, useMemo } from 'react';
import { supabase } from '../../lib/supabase';
import type { Analysis } from '../../types/models';
import { useAnalysisHistory } from '../analysis/hooks/useAnalysisHistory';
import { formatTimeAgo } from '../../lib/utils';
import { useNavigate } from 'react-router-dom';
// Import Lucide Icon
import { ClipboardList, ExternalLink, TrendingUp, ArrowRight } from 'lucide-react'; 
// Import Shadcn UI components
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

// Professional score color classes
function getScoreColorClasses(score: number): string {
  if (score >= 8) return "bg-green-500 text-white"; 
  if (score >= 4) return "bg-yellow-500 text-yellow-900"; 
  return "bg-red-500 text-white";
}

interface AnalysisWithDetails extends Analysis {
  candidateName?: string;
  jobTitle?: string;
}

interface RecentActivityProps {
  projectId?: string | null;
  onNavigateToAnalysis?: () => void;
  onNavigateToHistory?: () => void;
}

export function RecentActivity({ projectId, onNavigateToAnalysis, onNavigateToHistory }: RecentActivityProps) {
  const { analyses, isLoading } = useAnalysisHistory();
  const [analysesWithDetails, setAnalysesWithDetails] = useState<AnalysisWithDetails[]>([]);
  const [isLoadingDetails, setIsLoadingDetails] = useState(true);
  const navigate = useNavigate();

  // Filter analyses by project when projectId is provided
  // Note: project_id field is added dynamically and not in the base Analysis type yet
  const filteredAnalyses = useMemo(() => {
    if (!projectId) return analyses;
    return analyses.filter((analysis: any) => analysis.project_id === projectId);
  }, [analyses, projectId]);

  useEffect(() => {
    async function fetchDetails() {
      if (!filteredAnalyses.length) {
        setIsLoadingDetails(false);
        return;
      }

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
          setIsLoadingDetails(false);
          console.warn("Cannot fetch recent activity details without user.");
          return;
      }

      const detailedAnalyses = await Promise.all(
        filteredAnalyses.slice(0, 5).map(async (analysis) => { // Limit to 5 for recent activity view
          const { data: candidateData } = await supabase
            .from('candidates')
            .select('name')
            .eq('id', analysis.candidate_id)
            .eq('user_id', user.id)
            .maybeSingle();

          const { data: jobData } = await supabase
            .from('jobs')
            .select('title')
            .eq('id', analysis.job_id)
            .eq('user_id', user.id)
            .maybeSingle();

          return {
            ...analysis,
            candidateName: candidateData?.name || '-',
            jobTitle: jobData?.title || '-',
          };
        })
      );

      setAnalysesWithDetails(detailedAnalyses);
      setIsLoadingDetails(false);
    }

    if (filteredAnalyses.length > 0) {
      fetchDetails();
    } else {
      setIsLoadingDetails(false);
    }
  }, [filteredAnalyses]);

  // Helper function to handle navigation
  const handleNavigateToAnalysis = () => {
    if (onNavigateToAnalysis) {
      onNavigateToAnalysis();
    } else {
      navigate('/?tab=analysis&subTab=new-analysis');
    }
  };

  const handleNavigateToHistory = () => {
    if (onNavigateToHistory) {
      onNavigateToHistory();
    } else {
      navigate('/?tab=analysis&subTab=history');
    }
  };

  // Calculate activity stats based on filtered analyses
  const activityStats = useMemo(() => {
    const totalAnalyses = filteredAnalyses.length;
    const oneWeekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
    const recentAnalyses = filteredAnalyses.filter(a => 
      new Date(a.created_at || '') > oneWeekAgo
    ).length;
    
    const averageScore = filteredAnalyses.length > 0 
      ? filteredAnalyses.reduce((sum, a) => sum + a.fit_score, 0) / filteredAnalyses.length 
      : 0;

    return {
      totalAnalyses,
      recentAnalyses,
      averageScore
    };
  }, [filteredAnalyses]);

  // Loading State using Skeletons
  if (isLoading || isLoadingDetails) {
    return (
      <div className="space-y-4 pt-2 min-h-[200px]">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="flex items-center space-x-4">
            <Skeleton className="h-9 w-9 rounded-lg" />
            <div className="space-y-1.5 flex-1">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-3 w-1/2" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Empty State with Call-to-Action
  if (analysesWithDetails.length === 0) {
    return (
      <div className="text-center text-slate-500 py-8 flex flex-col items-center min-h-[200px] justify-center space-y-4">
        <ClipboardList className="w-12 h-12 text-slate-400" />
        <div className="space-y-2">
          <p className="text-sm font-medium text-slate-600">
            {projectId ? 'No activity for this project yet.' : 'No recent analyses found.'}
          </p>
          <p className="text-xs text-slate-500">
            {projectId 
              ? 'Upload CVs for this project to see candidate analysis activity here.'
              : 'Start by uploading a CV and Job Description to see activity here.'
            }
          </p>
        </div>
        <Button 
          size="sm" 
          variant="outline" 
          onClick={handleNavigateToAnalysis}
          className="mt-4 border-slate-200 hover:bg-slate-50"
        >
          <TrendingUp className="w-4 h-4 mr-2" />
          {projectId ? 'Analyze CV for Project' : 'Start Analysis'}
        </Button>
      </div>
    );
  }

  // Activity List with Enhanced Interactions
  return (
    <div className="space-y-4">
      {/* Activity Statistics */}
      {activityStats.totalAnalyses > 0 && (
        <div className="grid grid-cols-2 gap-4 mb-6 p-4 bg-slate-50 rounded-xl border border-slate-200">
          <div className="text-center space-y-1">
            <div className="text-2xl font-bold text-slate-900">{activityStats.recentAnalyses}</div>
            <div className="text-xs text-slate-600 font-medium">This Week</div>
          </div>
          <div className="text-center space-y-1">
            <div className="text-2xl font-bold text-slate-900">{activityStats.averageScore.toFixed(1)}</div>
            <div className="text-xs text-slate-600 font-medium">Avg Score</div>
          </div>
        </div>
      )}

      {/* Activity Items */}
      <div className="space-y-2 max-h-72 overflow-y-auto">
        {analysesWithDetails.map((analysis) => {
          const scoreColorClasses = getScoreColorClasses(analysis.fit_score);
          return (
            <div 
              key={analysis.id} 
              className="flex items-center space-x-4 p-4 hover:bg-slate-50 rounded-xl transition-all duration-200 cursor-pointer group border border-transparent hover:border-slate-200"
              onClick={handleNavigateToHistory}
            >
              <Badge className={cn(
                "w-12 h-12 flex items-center justify-center rounded-lg text-sm font-bold transition-all duration-200",
                scoreColorClasses
              )}>
                {analysis.fit_score}
              </Badge>
              <div className="min-w-0 flex-1 space-y-1">
                <p className="text-sm font-semibold text-slate-900 truncate group-hover:text-slate-700 transition-colors">
                  {analysis.candidateName || 'Unknown Candidate'}
                </p>
                <p className="text-xs text-slate-600 truncate">
                  {analysis.jobTitle || 'Unknown Position'} • {formatTimeAgo(analysis.created_at)}
                </p>
              </div>
              <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-slate-600 transition-colors" />
            </div>
          );
        })}
      </div>

      {/* View All Button */}
      {analysesWithDetails.length > 0 && (
        <div className="pt-4 border-t border-slate-200">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleNavigateToHistory}
            className="w-full justify-center text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg"
          >
            {projectId ? 'View All Project Analyses' : 'View All Analyses'}
            <ExternalLink className="w-4 h-4 ml-2" />
          </Button>
        </div>
      )}
    </div>
  );
}